import { useEffect, useState, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { apiClient } from '@/services/api'
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon,
  BookOpenIcon,
  CodeBracketIcon,
  CogIcon,
  RocketLaunchIcon,
  ClipboardDocumentListIcon,
  Bars3BottomLeftIcon,
  XMarkIcon,
  CommandLineIcon,
  FolderArrowDownIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import toast from 'react-hot-toast'

interface DocFile {
  name: string
  path: string
  category: string
  title: string
}

interface DocContent {
  name: string
  path: string
  category: string
  title: string
  content: string
}

interface DocsIndex {
  categories: Record<string, DocFile[]>
  total_files: number
}

interface TableOfContentsItem {
  id: string
  text: string
  level: number
}

// Category configuration with icons and colors - Developer focused
const CATEGORY_CONFIG: Record<string, { icon: any; color: string; gradient: string; description: string }> = {
  'Getting Started': { icon: RocketLaunchIcon, color: 'text-blue-600', gradient: 'from-blue-500 to-blue-600', description: 'Quick start and setup guides' },
  'API Reference': { icon: CodeBracketIcon, color: 'text-purple-600', gradient: 'from-purple-500 to-purple-600', description: 'Complete API documentation' },
  'SDK & Integration': { icon: ClipboardDocumentListIcon, color: 'text-orange-600', gradient: 'from-orange-500 to-orange-600', description: 'SDKs and integration guides' },
  'Architecture': { icon: CogIcon, color: 'text-slate-600', gradient: 'from-slate-500 to-slate-600', description: 'System architecture and design' },
  'Guides': { icon: BookOpenIcon, color: 'text-emerald-600', gradient: 'from-emerald-500 to-emerald-600', description: 'Feature guides and tutorials' },
}

const CATEGORY_ORDER = ['Getting Started', 'API Reference', 'SDK & Integration', 'Architecture', 'Guides']

// Extract table of contents from markdown
function extractTableOfContents(content: string): TableOfContentsItem[] {
  const headingRegex = /^(#{1,3})\s+(.+)$/gm
  const toc: TableOfContentsItem[] = []
  let match

  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[1].length
    const text = match[2].replace(/\*\*/g, '').replace(/`/g, '')
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    toc.push({ id, text, level })
  }

  return toc
}

export default function DocsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [docsIndex, setDocsIndex] = useState<DocsIndex | null>(null)
  const [selectedDoc, setSelectedDoc] = useState<DocContent | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingDoc, setIsLoadingDoc] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(CATEGORY_ORDER))
  const [error, setError] = useState<string | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [activeHeading, setActiveHeading] = useState<string>('')

  // Table of contents for current document
  const tableOfContents = useMemo(() => {
    if (!selectedDoc) return []
    return extractTableOfContents(selectedDoc.content)
  }, [selectedDoc])

  // Fetch docs index on mount
  useEffect(() => {
    const fetchIndex = async () => {
      try {
        setIsLoading(true)
        const index = await apiClient.getDocsIndex()
        setDocsIndex(index)

        const docPath = searchParams.get('doc')
        if (docPath) {
          await loadDocument(docPath)
        } else if (index.categories['General']?.length > 0) {
          const indexDoc = index.categories['General'].find(d => d.name === 'INDEX.md')
          if (indexDoc) {
            await loadDocument(indexDoc.path)
          }
        }
      } catch (err: any) {
        console.error('Failed to load docs index:', err)
        setError(err.message || 'Failed to load documentation')
      } finally {
        setIsLoading(false)
      }
    }
    fetchIndex()
  }, [])

  // Track scroll position for active heading
  useEffect(() => {
    const handleScroll = () => {
      const headings = document.querySelectorAll('#doc-content h1, #doc-content h2, #doc-content h3')
      let current = ''

      headings.forEach((heading) => {
        const rect = heading.getBoundingClientRect()
        if (rect.top <= 100) {
          current = heading.id
        }
      })

      setActiveHeading(current)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [selectedDoc])

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        document.getElementById('docs-search')?.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const loadDocument = async (path: string) => {
    try {
      setIsLoadingDoc(true)
      const doc = await apiClient.getDocContent(path)
      setSelectedDoc(doc)
      setSearchParams({ doc: path })
      setSidebarOpen(false)
      window.scrollTo(0, 0)
    } catch (err: any) {
      console.error('Failed to load document:', err)
      toast.error('Failed to load document')
    } finally {
      setIsLoadingDoc(false)
    }
  }

  const handleDocSelect = (doc: DocFile) => {
    loadDocument(doc.path)
  }

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  const handleDownloadMarkdown = () => {
    if (!selectedDoc) return
    const blob = new Blob([selectedDoc.content], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    // Use ziro-agent-dashboard- prefix for consistent naming
    const baseName = selectedDoc.name.replace('.md', '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    a.download = `ziro-agent-dashboard-${baseName}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success('Markdown downloaded')
  }

  const handleDownloadPDF = useCallback(async () => {
    if (!selectedDoc) return
    const toastId = toast.loading('Generating PDF...')

    try {
      const html2pdf = (await import('html2pdf.js')).default
      const element = document.getElementById('doc-content')
      if (!element) {
        toast.dismiss(toastId)
        toast.error('Could not find document content')
        return
      }

      const clone = element.cloneNode(true) as HTMLElement
      clone.style.padding = '40px'
      clone.style.maxWidth = '800px'
      clone.style.margin = '0 auto'
      clone.style.fontSize = '14px'
      clone.style.lineHeight = '1.6'

      // Use ziro-agent-dashboard- prefix for consistent naming
      const baseName = selectedDoc.name.replace('.md', '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
      const opt = {
        margin: [15, 15, 15, 15] as [number, number, number, number],
        filename: `ziro-agent-dashboard-${baseName}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      }

      await html2pdf().set(opt).from(clone).save()
      toast.dismiss(toastId)
      toast.success('PDF downloaded')
    } catch (err) {
      console.error('PDF generation failed:', err)
      toast.dismiss(toastId)
      toast.error('Failed to generate PDF')
    }
  }, [selectedDoc])

  const handleCopyMarkdown = () => {
    if (!selectedDoc) return
    navigator.clipboard.writeText(selectedDoc.content)
    toast.success('Copied to clipboard')
  }

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Filter documents based on search
  const getFilteredCategories = (): Record<string, DocFile[]> => {
    if (!docsIndex) return {}
    if (!searchQuery.trim()) return docsIndex.categories

    const query = searchQuery.toLowerCase()
    const filtered: Record<string, DocFile[]> = {}

    for (const [category, docs] of Object.entries(docsIndex.categories)) {
      const matchingDocs = docs.filter(
        doc =>
          doc.title.toLowerCase().includes(query) ||
          doc.name.toLowerCase().includes(query) ||
          doc.category.toLowerCase().includes(query)
      )
      if (matchingDocs.length > 0) {
        filtered[category] = matchingDocs
      }
    }

    return filtered
  }

  const filteredCategories = getFilteredCategories()
  const sortedCategories = Object.keys(filteredCategories).sort((a, b) => {
    const aIndex = CATEGORY_ORDER.indexOf(a)
    const bIndex = CATEGORY_ORDER.indexOf(b)
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b)
    if (aIndex === -1) return 1
    if (bIndex === -1) return -1
    return aIndex - bIndex
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading documentation...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/15 flex items-center justify-center">
            <DocumentTextIcon className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Unable to Load Documentation</h3>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-muted/50">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed bottom-4 right-4 z-50 p-3 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors"
      >
        {sidebarOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3BottomLeftIcon className="w-6 h-6" />}
      </button>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={clsx(
            'fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-card border-r border-border overflow-hidden flex flex-col transition-transform duration-300',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          )}
        >
          {/* Sidebar header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                  <BookOpenIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-semibold text-foreground">Developer Docs</h1>
                  <p className="text-xs text-muted-foreground">{docsIndex?.total_files || 0} documents</p>
                </div>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                id="docs-search"
                type="text"
                placeholder="Search docs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-12 py-2 text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-muted/50"
              />
              <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground bg-muted rounded">
                <CommandLineIcon className="w-2.5 h-2.5" />K
              </kbd>
            </div>

            {/* Download All Button */}
            <a
              href={apiClient.getDocsDownloadAllUrl()}
              download
              className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium text-foreground bg-muted/50 border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <FolderArrowDownIcon className="w-4 h-4" />
              Download All Docs
            </a>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-3 space-y-1">
            {sortedCategories.map((category) => {
              const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG['General']
              const Icon = config.icon
              const isExpanded = expandedCategories.has(category)

              return (
                <div key={category}>
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors group"
                  >
                    <div className={clsx('w-5 h-5 rounded flex items-center justify-center', config.color)}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="flex-1 text-left">{category}</span>
                    <span className="text-xs text-muted-foreground mr-1">
                      {filteredCategories[category]?.length || 0}
                    </span>
                    {isExpanded ? (
                      <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="ml-5 mt-1 space-y-0.5 border-l-2 border-border pl-3">
                      {filteredCategories[category]?.map((doc) => {
                        const isSelected = selectedDoc?.path === doc.path
                        return (
                          <div key={doc.path}>
                            <button
                              onClick={() => handleDocSelect(doc)}
                              className={clsx(
                                'w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md transition-all text-left',
                                isSelected
                                  ? 'bg-primary-50 text-primary-700 font-medium'
                                  : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                              )}
                            >
                              <span className="truncate">{doc.title}</span>
                            </button>
                            {/* Subsections (Table of Contents) for selected doc */}
                            {isSelected && tableOfContents.length > 0 && (
                              <div className="ml-3 mt-1 space-y-0.5 border-l border-border pl-2">
                                {tableOfContents.map((item) => (
                                  <button
                                    key={item.id}
                                    onClick={() => scrollToHeading(item.id)}
                                    className={clsx(
                                      'w-full text-left text-xs py-1 px-2 rounded transition-colors truncate',
                                      item.level === 1 && 'font-medium',
                                      item.level === 2 && 'pl-3',
                                      item.level === 3 && 'pl-5',
                                      activeHeading === item.id
                                        ? 'text-primary-600 bg-primary-50'
                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                                    )}
                                    title={item.text}
                                  >
                                    {item.text}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/20 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0">
          {isLoadingDoc ? (
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-muted-foreground mt-4">Loading document...</p>
              </div>
            </div>
          ) : selectedDoc ? (
            <div>
              {/* Document content */}
              <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                  <span className="hover:text-foreground cursor-pointer" onClick={() => {
                    const indexDoc = docsIndex?.categories['General']?.find(d => d.name === 'INDEX.md')
                    if (indexDoc) loadDocument(indexDoc.path)
                  }}>
                    Docs
                  </span>
                  <ChevronRightIcon className="w-4 h-4" />
                  <span className={CATEGORY_CONFIG[selectedDoc.category]?.color || 'text-muted-foreground'}>
                    {selectedDoc.category}
                  </span>
                  <ChevronRightIcon className="w-4 h-4" />
                  <span className="text-foreground font-medium truncate">{selectedDoc.title}</span>
                </nav>

                {/* Document header */}
                <header className="mb-8">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div>
                      <div className={clsx(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium mb-3',
                        `bg-gradient-to-r ${CATEGORY_CONFIG[selectedDoc.category]?.gradient || 'from-gray-500 to-gray-600'} text-white`
                      )}>
                        {(() => {
                          const Icon = CATEGORY_CONFIG[selectedDoc.category]?.icon || DocumentTextIcon
                          return <Icon className="w-3 h-3" />
                        })()}
                        {selectedDoc.category}
                      </div>
                      <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight">
                        {selectedDoc.title}
                      </h1>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={handleCopyMarkdown}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted/50 transition-colors"
                        title="Copy markdown"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                        <span className="hidden sm:inline">Copy</span>
                      </button>
                      <button
                        onClick={handleDownloadMarkdown}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted/50 transition-colors"
                        title="Download as Markdown"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        <span className="hidden sm:inline">MD</span>
                      </button>
                      <button
                        onClick={handleDownloadPDF}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                        title="Download as PDF"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        <span className="hidden sm:inline">PDF</span>
                      </button>
                    </div>
                  </div>
                </header>

                {/* Markdown content */}
                <div
                  id="doc-content"
                  className="prose prose-slate max-w-none
                    prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-4
                    prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline prose-a:font-medium
                    prose-strong:text-foreground prose-strong:font-semibold
                    prose-code:text-sm prose-code:bg-muted prose-code:text-primary-700 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-[''] prose-code:after:content-[''] prose-code:font-medium
                    prose-pre:bg-[#1e1e1e] prose-pre:rounded-xl prose-pre:shadow-lg prose-pre:my-6
                    prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:bg-primary-50 prose-blockquote:py-3 prose-blockquote:px-5 prose-blockquote:rounded-r-lg prose-blockquote:not-italic prose-blockquote:my-6
                    prose-ul:text-muted-foreground prose-ul:my-4 prose-ul:space-y-1
                    prose-ol:text-muted-foreground prose-ol:my-4 prose-ol:space-y-1
                    prose-li:marker:text-primary-500 prose-li:my-1
                    prose-table:text-sm prose-table:my-6
                    prose-th:bg-muted prose-th:font-semibold prose-th:text-foreground prose-th:py-3 prose-th:px-4
                    prose-td:border-border prose-td:py-3 prose-td:px-4
                    prose-tr:border-b prose-tr:border-border
                    prose-img:rounded-xl prose-img:shadow-md prose-img:my-6
                    prose-hr:border-border prose-hr:my-10
                  "
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ children }) => {
                        const id = String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                        return (
                          <div className="flex items-start gap-3 mt-8 mb-6 pb-4 border-b-2 border-primary-200">
                            <span className="w-1.5 h-8 mt-1 bg-gradient-to-b from-primary-500 to-primary-600 rounded-full flex-shrink-0" />
                            <h1 id={id} className="text-2xl font-bold text-foreground scroll-mt-20">{children}</h1>
                          </div>
                        )
                      },
                      h2: ({ children }) => {
                        const id = String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                        return (
                          <div className="flex items-center gap-3 mt-12 mb-4 pt-6 border-t border-border">
                            <span className="w-8 h-0.5 bg-primary-400 rounded-full flex-shrink-0" />
                            <h2 id={id} className="text-xl font-semibold text-foreground scroll-mt-20">{children}</h2>
                          </div>
                        )
                      },
                      h3: ({ children }) => {
                        const id = String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                        return (
                          <h3 id={id} className="text-lg font-semibold text-foreground mt-8 mb-3 pl-3 border-l-4 border-primary-400 scroll-mt-20">
                            {children}
                          </h3>
                        )
                      },
                      h4: ({ children }) => {
                        const id = String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                        return (
                          <h4 id={id} className="text-base font-medium text-muted-foreground mt-6 mb-2 uppercase tracking-wide scroll-mt-20">
                            {children}
                          </h4>
                        )
                      },
                      code({ className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '')
                        const inline = !match
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={oneDark as any}
                            language={match[1]}
                            PreTag="div"
                            customStyle={{
                              margin: 0,
                              borderRadius: '0.75rem',
                              fontSize: '0.875rem',
                            }}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        )
                      },
                      table({ children }) {
                        return (
                          <div className="overflow-x-auto rounded-lg border border-border my-6">
                            <table className="min-w-full divide-y divide-gray-200">{children}</table>
                          </div>
                        )
                      },
                      a({ href, children, ...props }) {
                        const isExternal = href?.startsWith('http')
                        return (
                          <a
                            href={href}
                            target={isExternal ? '_blank' : undefined}
                            rel={isExternal ? 'noopener noreferrer' : undefined}
                            {...props}
                          >
                            {children}
                          </a>
                        )
                      },
                    }}
                  >
                    {selectedDoc.content}
                  </ReactMarkdown>
                </div>
              </article>
            </div>
          ) : (
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center max-w-md px-4">
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                  <BookOpenIcon className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Welcome to Documentation</h2>
                <p className="text-muted-foreground mb-6">
                  Select a document from the sidebar to get started, or use the search to find what you're looking for.
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {['Getting Started', 'API Reference', 'Guides'].map((cat) => {
                    const config = CATEGORY_CONFIG[cat]
                    const Icon = config?.icon || DocumentTextIcon
                    return (
                      <button
                        key={cat}
                        onClick={() => {
                          setExpandedCategories(prev => new Set([...prev, cat]))
                          const firstDoc = docsIndex?.categories[cat]?.[0]
                          if (firstDoc) loadDocument(firstDoc.path)
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <Icon className={clsx('w-4 h-4', config?.color)} />
                        {cat}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
